'use client'

import { InfoCircle16, X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  InfoBanner,
  Label,
  Navigation,
  Note,
  SelectionGroup,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getReturnShippingOptions,
  createCustomReturnShipment,
  ComplaintModel,
  transformGetReturnShippingOptionsResp,
  PricingSummaryModel,
  ReturnShippingOptionModel,
  ReturnShippingOption,
  ReturnShippingOptionPricing,
  clickEvent,
  viewEvent,
  getEscrowRefundEstimate,
  transformEscrowRefundEstimateResp,
  formatCurrencyAmount,
  getEstimatedRefundValues,
  transformEstimatedRefundValuesDto,
  EstimatedRefundValuesModel,
  EstimatedRefundValuesItem,
} from '@marketplace-web/complaints/complaints-data'

import { isComplaintOrderSellerBusinessUser } from '../../pages/ComplaintCreation/utils'
import GenericErrorModal from '../GenericErrorModal'
import ComplaintOrderItems from '../RequestReturnModal/ComplaintOrderItems'
import ShipmentItems from '../RequestReturnModal/ShipmentItems'
import { findShippingOption, ModalKeys } from '../../utils/general'
import ShippingDiscountModal from '../ShippingDiscountModal'
import BuyerRefundInformation from '../BuyerRefundInformation'
import BuyerCurrencyConversionModal from '../BuyerCurrencyConversionModal'

type Props = {
  isOpen: boolean
  complaint?: ComplaintModel | null
  isComplaintLoading: boolean
  onClose: () => void
  onContinue: () => void
}

const ReturnShippingOptionsModal = ({
  isOpen,
  complaint,
  isComplaintLoading,
  onClose,
  onContinue,
}: Props) => {
  const { locale } = useIntl()
  const { track } = useTracking()
  const translate = useTranslate('conversation.actions.return_shipping_options.modal')
  const currencyConversionTranslation = useTranslate('checkout.order_summary')
  const asset = useAsset('/assets/shipping')

  const { conversation } = useConversationContext()
  const transactionId = conversation?.transaction?.id
  const isBundle = conversation?.transaction?.isBundle
  const complaintOrderItemsIds =
    complaint?.complaintOrderItems?.map(item => item.orderItemId).toString() || null
  const { showAllItemsReturnBanner } = complaint || {}

  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'

  const [selectedShippingOption, setSelectedShippingOption] = useState<
    ReturnShippingOption | undefined
  >()
  const [activeModal, setActiveModal] = useState<ModalKeys | null>()

  const {
    fetch: fetchRefundEstimate,
    transformedData: refundEstimate,
    isLoading: isRefundEstimateLoading,
  } = useFetch(getEscrowRefundEstimate, transformEscrowRefundEstimateResp)

  const { fetch: fetchEstimatedRefundValues, transformedData: estimatedRefundValues } = useFetch(
    getEstimatedRefundValues,
    transformEstimatedRefundValuesDto,
  )

  const {
    fetch: fetchReturnShippingOptions,
    transformedData: returnShippingOptionsData,
    isLoading: isReturnShippingOptionsLoading,
  } = useFetch(getReturnShippingOptions, transformGetReturnShippingOptionsResp)

  const fetchData = useCallback(async () => {
    if (!isOpen || !transactionId) return
    setActiveModal(ModalKeys.ReturnShippingOptionsModal)

    const { error } = await fetchReturnShippingOptions(transactionId)

    if (complaint) await fetchEstimatedRefundValues(complaint.id)

    if (error) setActiveModal(ModalKeys.GenericErrorModal)
  }, [isOpen, transactionId, fetchReturnShippingOptions, complaint, fetchEstimatedRefundValues])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!returnShippingOptionsData) return

    const integratedReturnShippingOption = findShippingOption(
      returnShippingOptionsData.returnShippingOptions,
      ReturnShippingOption.Integrated,
    )
    const customReturnShippingOption = findShippingOption(
      returnShippingOptionsData.returnShippingOptions,
      ReturnShippingOption.Custom,
    )

    if (integratedReturnShippingOption) {
      setSelectedShippingOption(ReturnShippingOption.Integrated)
    } else if (customReturnShippingOption) {
      setSelectedShippingOption(ReturnShippingOption.Custom)
    }
  }, [returnShippingOptionsData])

  const isUnifiedComplaintFlow = complaint?.complaintOrderItems?.length
  const integratedReturnShippingOption = findShippingOption(
    returnShippingOptionsData?.returnShippingOptions,
    ReturnShippingOption.Integrated,
  ) as ReturnShippingOptionModel
  const customReturnShippingOption = findShippingOption(
    returnShippingOptionsData?.returnShippingOptions,
    ReturnShippingOption.Custom,
  ) as ReturnShippingOptionModel
  const isIntegratedReturnShippingOptionSelected =
    selectedShippingOption === ReturnShippingOption.Integrated
  const isIntegratedAndPaidBySeller =
    integratedReturnShippingOption && integratedReturnShippingOption?.sellerPaidForReturn

  useEffect(() => {
    if (!isOpen) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      viewEvent({
        screen: 'return_order',
        target: 'return_order',
        targetDetails,
      }),
    )
  }, [isOpen, track, transactionId])

  const { fetch: fetchCreateReturnShipment, isLoading: isCreateReturnShipmentLoading } = useFetch(
    createCustomReturnShipment,
  )

  const isMultipleShippingOptions = () => {
    if (!returnShippingOptionsData?.returnShippingOptions) return false

    return returnShippingOptionsData.returnShippingOptions.length > 1
  }

  const selectReturnShippingMethod = (returnShippingOption: ReturnShippingOption) => () => {
    setSelectedShippingOption(returnShippingOption)
  }

  const showCurrencyConversion = () => {
    if (transactionId && complaintOrderItemsIds) {
      fetchRefundEstimate({
        orderId: transactionId,
        orderItemIds: complaintOrderItemsIds,
        integratedShipping:
          isIntegratedReturnShippingOptionSelected &&
          !integratedReturnShippingOption?.sellerPaidForReturn,
      })
    }

    setActiveModal(ModalKeys.ReturnCurrencyConversionModal)
  }

  const handleModalClose = () => {
    setSelectedShippingOption(undefined)
    setActiveModal(null)
    onClose()
  }

  const renderNavigation = () => {
    return (
      <>
        <Navigation
          right={
            <Button
              iconName={X24}
              theme="amplified"
              styling="flat"
              testId="return-shipping-options-close-button"
              onClick={handleModalClose}
            />
          }
          body={
            <Text
              as="h2"
              type="title"
              width="parent"
              alignment="center"
              text={translate('title')}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderShowAllItemsReturnInfoBanner = () => {
    if (!showAllItemsReturnBanner) return null

    return <InfoBanner body={<Text as="span" text={translate('all_items_return_banner')} />} />
  }

  const renderMultipleShippingOptionsTitle = () => (
    <Cell title={<Text as="h1" type="heading" text={translate('shipping_method_title')} />} />
  )

  const renderOneShippingOptionTitle = () => (
    <Label type="stacked" text={translate('one_shipping_method_title')} />
  )

  const renderTitle = () =>
    isMultipleShippingOptions()
      ? renderMultipleShippingOptionsTitle()
      : renderOneShippingOptionTitle()

  const renderShippingItems = () => {
    if (isComplaintLoading) return <ContentLoader styling={ContentLoader.Styling.Wide} />

    if (complaint?.complaintOrderItems?.length)
      return <ComplaintOrderItems complaintOrderItems={complaint.complaintOrderItems} />

    return <ShipmentItems transactionId={transactionId} />
  }

  const renderItemVerificationInfoBanner = () => {
    if (!integratedReturnShippingOption?.withAuthenticityCheck) return null

    return <InfoBanner body={<Text as="span" text={translate('offline_verification')} />} />
  }

  const renderElectronicsVerificationInfoBanner = () => {
    if (!integratedReturnShippingOption?.withElectronicsVerification) return null

    return (
      <InfoBanner body={<Text as="span" text={translate('electronics_verification')} html />} />
    )
  }

  const renderItemsNote = () => {
    if (!isBundle) return null

    if (isUnifiedComplaintFlow || isComplaintLoading) return null

    return <Note text={translate('bundle_items_note')} />
  }

  const handleShippingDiscountInfoClick = () => {
    track(
      clickEvent({
        screen: 'return_order',
        target: 'shipping_discount_details',
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )

    setActiveModal(ModalKeys.ShippingDiscountModal)
  }

  const renderShippingPrice = (pricingSummary: PricingSummaryModel) => {
    if (pricingSummary.pricing !== ReturnShippingOptionPricing.Postage || !pricingSummary.price)
      return null

    const { price, priceBeforeDiscount } = pricingSummary

    if (!priceBeforeDiscount) {
      return (
        <Text
          as="h2"
          key={pricingSummary.pricing}
          type="title"
          text={formatCurrencyAmount(pricingSummary.price, locale)}
        />
      )
    }

    return (
      <div className="u-flexbox u-align-items-center" key={price.amount}>
        <Text
          as="span"
          text={`${formatCurrencyAmount(price, locale)}`}
          type="body"
          bold
          testId="return-shipping-options-modal-price-after-discount"
        />
        <Spacer orientation="vertical" size="small" />
        <Text
          as="h3"
          text={formatCurrencyAmount(priceBeforeDiscount, locale)}
          type="subtitle"
          strikethrough
          testId="return-shipping-options-modal-price-before-discount"
        />
        <Spacer orientation="vertical" size="small" />
        <button
          type="button"
          className="u-flexbox u-align-items-center u-cursor-pointer"
          onClick={handleShippingDiscountInfoClick}
          aria-label={translate('a11y.shipping_discount_info_icon')}
        >
          <Icon
            name={InfoCircle16}
            color="greyscale-level-2"
            testId="return-shipping-options-modal-discount-info-icon"
          />
        </button>
      </div>
    )
  }

  const renderCarrierDiscountInfo = () => {
    if (!integratedReturnShippingOption) return null

    const postagePrice = integratedReturnShippingOption.pricingSummary?.find(
      pricingSummary => pricingSummary.pricing === ReturnShippingOptionPricing.Postage,
    )

    if (!postagePrice?.discount) return null

    return (
      <>
        <Spacer orientation="vertical" size="small" />
        <Text
          as="span"
          text={`(-${postagePrice.discount.percent}%)`}
          theme="warning"
          testId="return-shipping-options-modal-discount-percent"
        />
      </>
    )
  }

  const renderIntegratedReturnShippingOption = () => {
    const { iconUrl, title, subtitle, pricingSummary, code, note } = integratedReturnShippingOption

    return (
      <SelectionGroup.SelectionItem
        key={code}
        showSelectionIndicator={isMultipleShippingOptions()}
        title={
          <div className="u-flexbox u-flex-direction-column">
            <div>
              <Image
                src={iconUrl}
                size="regular"
                styling="rounded"
                fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
              />
              <Spacer orientation="vertical" />
              <Text as="h2" type="title" text={title} />
              {renderCarrierDiscountInfo()}
            </div>
            <Spacer />
            {note ? (
              <Text as="h2" type="title" text={note} html />
            ) : (
              pricingSummary?.map(renderShippingPrice)
            )}
            <Spacer size="large" />
          </div>
        }
        body={<Text as="p" text={subtitle} />}
        onClick={selectReturnShippingMethod(code)}
        isSelected={code === selectedShippingOption}
        testId="return-shipping-option-integrated"
      />
    )
  }

  const renderCustomReturnShippingOption = () => {
    const { iconUrl, title, subtitle, code } = customReturnShippingOption

    return (
      <SelectionGroup.SelectionItem
        key={code}
        showSelectionIndicator={isMultipleShippingOptions()}
        title={
          <>
            <Image src={iconUrl} size="regular" styling="rounded" />
            <Spacer orientation="vertical" />
            <Text type="title" text={title} as="h2" />
            <Spacer size="large" />
          </>
        }
        body={<Text text={subtitle} as="p" />}
        onClick={selectReturnShippingMethod(code)}
        testId="return-shipping-option-custom"
        isSelected={code === selectedShippingOption}
      />
    )
  }

  const renderReturnShippingOptions = () => {
    if (!returnShippingOptionsData?.returnShippingOptions?.length) return null

    return (
      <SelectionGroup layout="default">
        {returnShippingOptionsData.returnShippingOptions.map(option => {
          if (option.code === ReturnShippingOption.Integrated) {
            return renderIntegratedReturnShippingOption()
          }

          return renderCustomReturnShippingOption()
        })}
      </SelectionGroup>
    )
  }

  const openRefund = () => {
    if (transactionId && complaintOrderItemsIds) {
      fetchRefundEstimate({
        orderId: transactionId,
        orderItemIds: complaintOrderItemsIds,
        integratedShipping:
          isIntegratedReturnShippingOptionSelected &&
          !integratedReturnShippingOption?.sellerPaidForReturn,
      })
    }

    setActiveModal(ModalKeys.BuyerRefundInformation)
  }

  const renderRefundInfoIcon = () => {
    if (!isUnifiedComplaintFlow) return null

    return (
      <>
        <Spacer orientation="vertical" />
        <button
          className="u-flexbox u-align-items-center"
          onClick={openRefund}
          type="button"
          title={translate('a11y.refund_info_icon')}
          aria-label={translate('a11y.refund_info_icon')}
        >
          <Icon name={InfoCircle16} color="greyscale-level-2" />
        </button>
      </>
    )
  }

  const renderCurrencyConvertionNote = () => {
    if (!estimatedRefundValues?.isConverted) return null

    return (
      <>
        <span>
          <button type="button" onClick={showCurrencyConversion}>
            <Text
              clickable
              theme="inverse"
              type="caption"
              text={currencyConversionTranslation('currency_conversion_details.learn_more_link')}
              as="span"
            />
          </button>
        </span>
        <Spacer orientation="vertical" size="small" />
        <Text
          type="caption"
          text={currencyConversionTranslation('currency_conversion_details.learn_more_about')}
          as="span"
        />
      </>
    )
  }

  const renderTotalPaid = () => {
    const { totalRefund } = estimatedRefundValues as EstimatedRefundValuesModel
    const { title, value } = totalRefund

    return (
      <Cell
        styling="tight"
        title={
          <div className="u-flexbox">
            <Text as="span" text={title} />
            {renderRefundInfoIcon()}
          </div>
        }
        suffix={<Text as="span" text={formatCurrencyAmount(value, locale)} />}
      />
    )
  }

  const renderDeductedRefund = () => {
    const { deductedRefund, totalRefund } = estimatedRefundValues as EstimatedRefundValuesModel
    const value =
      selectedShippingOption === ReturnShippingOption.Custom
        ? totalRefund.value
        : deductedRefund.value

    return (
      <>
        <Spacer size="large" />
        <Cell
          styling="tight"
          title={
            <div className="u-flexbox">
              <Text as="h2" type="title" text={deductedRefund.title} />
            </div>
          }
          suffix={
            <Text
              as="h2"
              type={Text.Type.Title}
              text={<Text as="span" text={formatCurrencyAmount(value, locale)} type="title" />}
            />
          }
        />
      </>
    )
  }

  const renderDeductibles = () => {
    const { deductedRefund } = estimatedRefundValues as EstimatedRefundValuesModel
    const { deductibles } = deductedRefund

    if (selectedShippingOption === ReturnShippingOption.Custom) return null

    if (isIntegratedAndPaidBySeller) {
      const returnShipping = deductibles.find(
        item => item.type === EstimatedRefundValuesItem.ReturnShipping,
      )

      if (!returnShipping) return null

      return (
        <Cell
          styling="tight"
          title={
            <div className="u-flexbox">
              <Text as="span" text={returnShipping.title} />
            </div>
          }
          suffix={<Text as="span" text={translate('paid_by_seller_note')} />}
        />
      )
    }

    return deductibles.map(item => (
      <Cell
        key={item.title}
        styling="tight"
        title={
          <div className="u-flexbox">
            <Text as="span" text={item.title} />
          </div>
        }
        suffix={<Text as="span" text={formatCurrencyAmount(item.value, locale)} />}
      />
    ))
  }

  const renderShippingPriceOption = () => {
    if (!estimatedRefundValues)
      return (
        <ContentLoader
          styling={ContentLoader.Styling.Wide}
          testId="return-shipping-option-price-loader"
        />
      )

    return (
      <>
        <Cell>
          <>
            {renderTotalPaid()}
            {renderDeductibles()}
            {renderDeductedRefund()}
          </>
          {renderCurrencyConvertionNote()}
        </Cell>
        <Divider />
      </>
    )
  }

  const trackSubmitClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
      selected_return_option: selectedShippingOption,
    }

    track(
      clickEvent({
        screen: 'return_order',
        target: 'confirm',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const handleSubmitClick = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (isReturnShippingOptionsLoading || isCreateReturnShipmentLoading) {
      event.preventDefault()

      return
    }

    trackSubmitClick()

    if (isIntegratedReturnShippingOptionSelected) {
      setActiveModal(null)
      onContinue()

      return
    }

    if (!transactionId || !selectedShippingOption) return

    const { error } = await fetchCreateReturnShipment({
      transactionId,
      returnShippingOption: selectedShippingOption,
    })

    if (error) {
      setActiveModal(ModalKeys.GenericErrorModal)

      return
    }

    handleModalClose()
  }

  const renderSubmit = () => {
    const isBuyerPayingForShipping = !integratedReturnShippingOption?.note

    const buttonText = isIntegratedReturnShippingOptionSelected
      ? integratedReturnShippingOption?.actionTitle
      : customReturnShippingOption?.actionTitle

    const buttonTestId = isIntegratedReturnShippingOptionSelected
      ? 'return-shipping-option-integrated-submit'
      : 'return-shipping-option-custom-submit'

    return (
      <>
        {isBuyerPayingForShipping && <Note text={translate('submit_note')} />}
        <Cell>
          <Button
            styling="filled"
            text={buttonText}
            isLoading={isReturnShippingOptionsLoading || isCreateReturnShipmentLoading}
            onClick={handleSubmitClick}
            testId={buttonTestId}
          />
        </Cell>
      </>
    )
  }

  const handleClose = () => {
    setActiveModal(ModalKeys.ReturnShippingOptionsModal)
  }

  const renderReturnShippingOptionsModal = () => {
    return (
      <Dialog show hasScrollableContent>
        <div className="u-fill-width">
          {renderNavigation()}
          <div className="modal-contained-content return-shipping-options-modal">
            {renderShowAllItemsReturnInfoBanner()}
            {renderShippingItems()}
            {renderItemsNote()}
            {renderItemVerificationInfoBanner()}
            {renderElectronicsVerificationInfoBanner()}
            {renderTitle()}
            {renderReturnShippingOptions()}
            {renderShippingPriceOption()}
            {renderSubmit()}
          </div>
        </div>
      </Dialog>
    )
  }

  const renderShippingDiscountModal = () => {
    const postagePrice = integratedReturnShippingOption?.pricingSummary?.find(
      pricingSummary => pricingSummary.pricing === ReturnShippingOptionPricing.Postage,
    )

    return (
      <ShippingDiscountModal
        isOpen
        onClose={handleClose}
        discount={postagePrice?.discount}
        lowestPriceIn30Days={postagePrice?.lowestPriceIn30days}
      />
    )
  }

  const renderCurrencyConversionModal = () => {
    return (
      <BuyerCurrencyConversionModal
        isOpen
        refundEstimate={refundEstimate}
        isOpenedInDialog
        onClose={handleClose}
        isLoading={isRefundEstimateLoading}
      />
    )
  }

  const renderBuyerRefundModal = () => {
    const isSellerBusinessUser = isComplaintOrderSellerBusinessUser(complaint?.orderType)

    return (
      <BuyerRefundInformation
        note={integratedReturnShippingOption?.note ? undefined : translate('submit_note')}
        selectedShippingOption={selectedShippingOption}
        isBundleComplaint={complaint?.isBundleOrder}
        transactionId={transactionId}
        refundEstimate={refundEstimate}
        isOpen
        onClose={handleClose}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpenedInDialog
        isLoading={isRefundEstimateLoading}
      />
    )
  }

  const renderActiveModal = () => {
    switch (activeModal) {
      case ModalKeys.ReturnShippingOptionsModal:
        return renderReturnShippingOptionsModal()
      case ModalKeys.ShippingDiscountModal:
        return renderShippingDiscountModal()
      case ModalKeys.ReturnCurrencyConversionModal:
        return renderCurrencyConversionModal()
      case ModalKeys.BuyerRefundInformation:
        return renderBuyerRefundModal()
      case ModalKeys.GenericErrorModal:
        return <GenericErrorModal isShown onClose={handleModalClose} />
      default:
        return null
    }
  }

  return renderActiveModal()
}

export default ReturnShippingOptionsModal
